import React, { useState, useEffect, useMemo } from 'react';
import { LoginPage } from '../pages/LoginPage';
import { Dashboard } from '../pages/Dashboard';
import { Tenants } from '../pages/Tenants';
import { Plans } from '../pages/Plans';
import { PlatformTemplates } from '../pages/PlatformTemplates';
import { StationTerminalSettings } from '../pages/StationTerminalSettings';
import { MosquittoOps } from '../pages/MosquittoOps';
import { APIKeysReport } from '../pages/APIKeysReport';
import { AuditLogs } from '../pages/AuditLogs';
import { BillingSettings } from '../pages/BillingSettings';
import { AISettings } from '../pages/AISettings';
import { StorageSettings } from '../pages/StorageSettings';
import { BrandingSettings } from '../pages/BrandingSettings';
import { HealthMonitoring } from '../pages/HealthMonitoring';
import { LicenseSettings } from '../pages/LicenseSettings';
import { PlatformNotificationBell } from '../components/PlatformNotificationBell';
import { Icons } from '../components/icons';
import { TENANT_PORTAL_URL } from '@gridorbit/shared';
import {
  fetchPublicBranding,
  resolveBrandLogoUrl,
  displayCompanyName,
  displayCompanyNameEn,
  DEFAULT_BRAND_LOGO,
} from '@gridorbit/shared';
import { HeaderUserMenu } from '../components/HeaderUserMenu';
import { SidebarVersion } from '../components/SidebarVersion';

const MENU_GROUPS = [
  {
    id: 'overview',
    title: null,
    items: [
      { key: 'dashboard', label: '工作台', icon: <Icons.Home /> },
    ],
  },
  {
    id: 'tenant-biz',
    title: '租户与商务',
    items: [
      { key: 'tenants', label: '租户管理', icon: <Icons.Users /> },
      { key: 'plans', label: '套餐方案', icon: <Icons.Plan /> },
      { key: 'billing', label: '支付配置', icon: <Icons.Payment /> },
      { key: 'api-keys', label: 'Open API', icon: <Icons.Key /> },
    ],
  },
  {
    id: 'content-terminal',
    title: '内容与终端',
    items: [
      { key: 'templates', label: '行业模板', icon: <Icons.Layers /> },
      { key: 'station-mqtt', label: '工位 MQTT', icon: <Icons.Monitor /> },
      { key: 'mosquitto-ops', label: 'MQTT 运维', icon: <Icons.CloudServer /> },
    ],
  },
  {
    id: 'platform',
    title: '平台配置',
    items: [
      { key: 'ai-settings', label: 'AI 服务', icon: <Icons.Zap /> },
      { key: 'storage-settings', label: '对象存储', icon: <Icons.Layers /> },
      { key: 'branding-settings', label: 'OEM 品牌', icon: <Icons.Plan /> },
    ],
  },
  {
    id: 'ops',
    title: '运维与合规',
    items: [
      { key: 'audit', label: '操作审计', icon: <Icons.Shield /> },
      { key: 'license', label: '软件授权', icon: <Icons.Key /> },
      { key: 'health', label: '系统监控', icon: <Icons.Monitor /> },
    ],
  },
];

const PAGES = {
  dashboard: <Dashboard />,
  tenants: <Tenants />,
  plans: <Plans />,
  templates: <PlatformTemplates />,
  'station-mqtt': <StationTerminalSettings />,
  'mosquitto-ops': <MosquittoOps />,
  billing: <BillingSettings />,
  'ai-settings': <AISettings />,
  'storage-settings': <StorageSettings />,
  'branding-settings': <BrandingSettings />,
  'api-keys': <APIKeysReport />,
  audit: <AuditLogs />,
  license: <LicenseSettings />,
  health: <HealthMonitoring />,
};

export default function AdminShell() {
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem('token'));
  const [page, setPage] = useState('dashboard');
  const [collapsed, setCollapsed] = useState(() =>
    Object.fromEntries(MENU_GROUPS.filter((g) => g.title).map((g) => [g.id, true]))
  );
  const [brandLogo, setBrandLogo] = useState(DEFAULT_BRAND_LOGO);
  const [brandCn, setBrandCn] = useState('经纬循道信息');
  const [brandEn, setBrandEn] = useState('GridOrbit');
  const [customLogo, setCustomLogo] = useState(false);

  useEffect(() => {
    fetchPublicBranding().then((b) => {
      setBrandLogo(resolveBrandLogoUrl(b.logo_url));
      setCustomLogo(!!(b.logo_url || '').trim());
      setBrandCn(displayCompanyName(b));
      setBrandEn(displayCompanyNameEn(b));
    });
  }, []);

  useEffect(() => {
    const h = (e) => setPage(e.detail);
    window.addEventListener('nav', h);
    return () => window.removeEventListener('nav', h);
  }, []);

  useEffect(() => {
    const onAuthExpired = () => setLoggedIn(false);
    window.addEventListener('gridorbit:auth-expired', onAuthExpired);
    return () => window.removeEventListener('gridorbit:auth-expired', onAuthExpired);
  }, []);

  // 进入某页时展开其所在分组，其余保持折叠
  useEffect(() => {
    const g = MENU_GROUPS.find((x) => x.items.some((i) => i.key === page));
    if (g?.title) {
      setCollapsed((prev) => ({ ...prev, [g.id]: false }));
    }
  }, [page]);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setLoggedIn(false);
  };

  const toggleGroup = (id) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const flatMenu = useMemo(
    () => MENU_GROUPS.flatMap((g) => g.items),
    []
  );
  const current = flatMenu.find((m) => m.key === page);
  const activeGroup = MENU_GROUPS.find((g) => g.items.some((i) => i.key === page));
  const headerTitle = current
    ? (activeGroup?.title ? `${activeGroup.title} · ${current.label}` : current.label)
    : '经纬循道信息 数据中台';

  if (!loggedIn || !localStorage.getItem('token')) {
    return <LoginPage onLogin={() => setLoggedIn(true)} />;
  }

  return (
    <div className="layout">
      <div className="sidebar">
        <div className="sidebar-logo">
          <img src={brandLogo} alt={brandEn} className="sidebar-logo-img brand-logo-img" />
          {customLogo ? (
          <div className="sidebar-logo-stack">
            <span className="sidebar-logo-cn">{brandCn}</span>
            <span className="sidebar-logo-en">{brandEn}</span>
          </div>
          ) : null}
        </div>
        <div className="sidebar-nav">
          {MENU_GROUPS.map((group) => {
            const isCollapsed = !!(group.title && collapsed[group.id]);
            const hasActive = group.items.some((i) => i.key === page);
            return (
              <div
                key={group.id}
                className={
                  'sidebar-group'
                  + (isCollapsed ? ' is-collapsed' : '')
                  + (hasActive ? ' has-active' : '')
                }
              >
                {group.title && (
                  <button
                    type="button"
                    className="sidebar-group-title"
                    onClick={() => toggleGroup(group.id)}
                    aria-expanded={!isCollapsed}
                  >
                    <span>{group.title}</span>
                    <Icons.ChevronDown w={12} h={12} />
                  </button>
                )}
                <div className="sidebar-group-items">
                  {group.items.map((m) => (
                    <div
                      key={m.key}
                      className={'sidebar-item' + (page === m.key ? ' active' : '')}
                      onClick={() => setPage(m.key)}
                    >
                      {m.icon}
                      <span>{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <SidebarVersion />
      </div>
      <div className="main">
        <div className="header">
          <div className="header-title">{headerTitle}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PlatformNotificationBell />
            <HeaderUserMenu
              userName="平台管理员"
              roleLabel="Platform Admin"
              onLogout={logout}
              items={[{
                key: 'tenant-portal',
                label: '租户后台',
                icon: <Icons.Monitor w={14} h={14} />,
                href: TENANT_PORTAL_URL,
                external: true,
              }]}
            />
          </div>
        </div>
        <div className="content">{PAGES[page] || PAGES.dashboard}</div>
      </div>
    </div>
  );
}
